// GolfDashboard.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { usePPCAuth, PPC_API_URL } from './PPCContext';

// ── Constants ──────────────────────────────────────────────────────────────────
const TODAY = new Date().toISOString().slice(0, 10);
const GOLF_GRAD = 'linear-gradient(135deg,#059669 0%,#1565c0 100%)';

const fmtD = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  const mn = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(day)} ${mn[parseInt(m)-1]} ${y.slice(2)}`;
};

const getPeriodRange = (date, per) => {
  if (!date) return { from: date, to: date };
  const d = new Date(date + 'T00:00:00');
  if (per === 'daily') return { from: date, to: date };
  if (per === 'weekly') {
    const day = d.getDay();
    const mon = new Date(d); mon.setDate(d.getDate() - ((day + 6) % 7));
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { from: mon.toISOString().slice(0,10), to: sun.toISOString().slice(0,10) };
  }
  if (per === 'monthly') {
    const from = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
    const last  = new Date(d.getFullYear(), d.getMonth()+1, 0);
    return { from, to: last.toISOString().slice(0,10) };
  }
  return { from: date, to: date };
};

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const calcKPI = (rows) => {
  const hon   = rows.reduce((s,r) => s + parseFloat(r.total_hon_quantity || 0), 0);
  const hl    = rows.reduce((s,r) => s + parseFloat(r.total_hl_quantity  || 0), 0);
  const grad  = rows.reduce((s,r) => s + parseFloat(r.grading_hl_quantity || 0), 0);
  const lots  = rows.reduce((s,r) => s + parseInt(r.total_lots       || 0), 0);
  const assoc = rows.reduce((s,r) => s + parseInt(r.male_associates  || 0) + parseInt(r.female_associates || 0), 0);
  const hrs   = rows.reduce((s,r) => s + parseFloat(r.working_hours  || 0), 0);
  const yld   = hon > 0 ? (hl / hon * 100) : 0;
  const prod  = hrs > 0 ? hl / hrs : 0;
  return { hon, hl, grad, lots, assoc, yld, prod };
};

// ── CSS ────────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
:root{--golf:#059669;--grad:${GOLF_GRAD};--g1:#1565c0;--g2:#d97706;--bg:#f0f2f5;--border:#e2e6ea;--text:#1a1d23;--muted:#6b7280;}
.gw *{box-sizing:border-box;margin:0;padding:0}
.gw{font-family:'DM Sans',sans-serif;background:var(--bg);min-height:100vh;font-size:13px;color:var(--text);padding-bottom:48px}
.g-hdr{background:var(--grad);padding:0 20px;height:52px;display:flex;justify-content:space-between;align-items:center;position:sticky;top:0;z-index:200;box-shadow:0 2px 14px rgba(5,150,105,.3)}
.g-hdr-l,.g-hdr-r{display:flex;align-items:center;gap:10px}
.g-hbtn{background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.28);color:#fff;padding:5px 12px;border-radius:6px;cursor:pointer;font-size:12px;font-family:inherit;font-weight:500}
.g-hbtn:hover{background:rgba(255,255,255,.25)}
.g-htitle{color:#fff;font-size:15px;font-weight:700;letter-spacing:.3px}
.g-hbadge{background:rgba(255,255,255,.2);color:#fff;font-size:10px;padding:3px 9px;border-radius:20px;font-weight:600}
.g-hdate{background:rgba(255,255,255,.95);border:none;border-radius:6px;padding:5px 10px;font-size:12px;font-family:inherit;color:var(--text);cursor:pointer;outline:none}
.g-hexp{padding:6px 14px;border-radius:7px;border:none;background:rgba(255,255,255,.9);color:var(--golf);font-family:inherit;font-size:12px;font-weight:700;cursor:pointer;display:flex;align-items:center;gap:5px}
.g-nav{background:#fff;border-bottom:2px solid var(--border);padding:0 20px;display:flex;position:sticky;top:52px;z-index:100;box-shadow:0 1px 4px rgba(0,0,0,.05)}
.g-ntab{padding:11px 18px;font-size:12px;font-weight:600;border:none;background:transparent;color:var(--muted);cursor:pointer;border-bottom:3px solid transparent;font-family:inherit;transition:all .18s;display:flex;align-items:center;gap:6px;white-space:nowrap}
.g-ntab.ag{color:var(--golf);border-bottom-color:var(--golf)}
.g-ntab.ar{color:#1565c0;border-bottom-color:#1565c0}
.g-main{padding:14px 20px;display:flex;flex-direction:column;gap:12px}
.g-chip{display:inline-flex;align-items:center;gap:6px;background:#d1fae5;color:#065f46;border:1px solid #6ee7b7;font-size:10px;font-weight:700;padding:3px 10px;border-radius:12px;letter-spacing:.4px}
.g-chip-r{display:inline-flex;align-items:center;gap:6px;background:#dbeafe;color:#1e40af;border:1px solid #93c5fd;font-size:10px;font-weight:700;padding:3px 10px;border-radius:12px;letter-spacing:.4px}
.g-ptabs{display:flex;gap:4px;align-items:center;flex-wrap:wrap}
.g-ptab{padding:7px 20px;border-radius:8px;border:1.5px solid var(--border);background:#fff;color:var(--muted);font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;transition:all .18s;display:flex;align-items:center;gap:5px}
.g-ptab.on{background:var(--grad);color:#fff;border-color:transparent;font-weight:700;box-shadow:0 3px 10px rgba(5,150,105,.25)}
.g-range{font-size:11px;color:var(--muted);padding:5px 12px;background:#fff;border-radius:7px;border:1px solid var(--border)}
.g-title-row{display:flex;justify-content:space-between;align-items:flex-end;flex-wrap:wrap;gap:10px}
.g-rp-wrap{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.g-rp-lbl{font-size:11px;color:var(--muted);font-weight:600}
.g-rp-date{padding:5px 9px;border-radius:6px;border:1.5px solid var(--border);font-family:'DM Mono',monospace;font-size:12px;color:var(--text);outline:none;cursor:pointer;transition:border-color .18s}
.g-rp-date:focus{border-color:var(--golf)}
.g-sdiv{display:flex;align-items:center;gap:10px;margin:2px 0}
.g-sdiv-line{flex:1;height:1px;background:var(--border)}
.g-kgrid{display:grid;grid-template-columns:repeat(5,1fr);gap:10px}
.g-kc{background:#fff;border:1.5px solid var(--border);border-radius:12px;padding:14px 16px;position:relative;overflow:hidden;transition:box-shadow .18s}
.g-kc:hover{box-shadow:0 4px 14px rgba(0,0,0,.09)}
.g-kc::before{content:'';position:absolute;top:0;left:0;width:4px;height:100%;border-radius:2px 0 0 2px}
.g-kc-a::before{background:#d97706}.g-kc-b::before{background:#1565c0}.g-kc-g::before{background:#059669}.g-kc-r::before{background:#dc2626}.g-kc-p::before{background:#7c3aed}
.g-kc-lbl{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--muted);margin-bottom:5px}
.g-kc-val{font-size:18px;font-weight:700;font-family:'DM Mono',monospace;line-height:1.2}
.g-kc-sub{font-size:10px;margin-top:3px;color:var(--muted)}
.g-card{background:#fff;border:1px solid var(--border);border-radius:12px;overflow:hidden;box-shadow:0 1px 4px rgba(0,0,0,.04)}
.g-chdr{padding:11px 16px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid var(--border);background:#fafbfc;flex-wrap:wrap;gap:8px}
.g-ctitle{font-size:13px;font-weight:600}
.g-sfbtns{display:flex;gap:3px}
.g-sfbtn{padding:4px 11px;border-radius:7px;border:1.5px solid var(--border);background:#fff;color:var(--muted);font-family:inherit;font-size:11px;font-weight:500;cursor:pointer;transition:all .18s}
.g-sfbtn.on{border-color:var(--golf);background:#d1fae5;color:#065f46;font-weight:700}
.g-btn-g1{padding:7px 14px;border-radius:7px;border:none;background:#1565c0;color:#fff;font-family:inherit;font-size:12px;font-weight:700;cursor:pointer}
.g-btn-g2{padding:7px 14px;border-radius:7px;border:none;background:#d97706;color:#fff;font-family:inherit;font-size:12px;font-weight:700;cursor:pointer}
.g-btn-exp{padding:6px 13px;border-radius:7px;border:none;background:var(--grad);color:#fff;font-family:inherit;font-size:12px;cursor:pointer;display:flex;align-items:center;gap:5px}
.g-bsm{padding:3px 9px;border:none;border-radius:5px;color:#fff;font-size:10px;font-weight:700;cursor:pointer;font-family:inherit}
.g-tbl-wrap{overflow-x:auto}
table.g-tbl{width:100%;border-collapse:collapse;font-size:11px;min-width:700px}
table.g-tbl thead th{padding:8px 10px;font-size:9px;font-weight:700;text-align:center;border:1px solid var(--border);white-space:nowrap;letter-spacing:.3px;text-transform:uppercase;background:#fafbfc;color:var(--muted);vertical-align:middle}
table.g-tbl thead th.tl{text-align:left;padding-left:13px}
table.g-tbl tbody tr{border-bottom:1px solid #f1f5f9;transition:background .12s}
table.g-tbl tbody tr:hover{background:#f0fdf4}
table.g-tbl td{padding:9px 10px;text-align:center;vertical-align:middle}
table.g-tbl td.tl{text-align:left;padding-left:13px;font-weight:600;font-size:12px}
table.g-tbl tfoot td{background:#f0f2f5;border-top:2px solid var(--border);font-weight:700;font-size:11px;text-align:center;padding:9px 10px}
table.g-tbl tfoot td.tl{text-align:left;padding-left:13px}
.g-mono{font-family:'DM Mono',monospace;font-size:11px}
.g-val-g{color:#059669;font-weight:600;font-family:'DM Mono',monospace}
.g-val-b{color:#1565c0;font-weight:600;font-family:'DM Mono',monospace}
.g-val-a{color:#d97706;font-weight:600;font-family:'DM Mono',monospace}
.g-val-r{color:#dc2626;font-weight:600;font-family:'DM Mono',monospace}
.g-shift-d{background:#fffbeb;color:#92400e;padding:2px 8px;border-radius:8px;font-size:10px;font-weight:700;display:inline-block}
.g-shift-g{background:#f0fdf4;color:#14532d;padding:2px 8px;border-radius:8px;font-size:10px;font-weight:700;display:inline-block}
.g-shift-n{background:#eff6ff;color:#1565c0;padding:2px 8px;border-radius:8px;font-size:10px;font-weight:700;display:inline-block}
.g-sec-banner{padding:8px 14px;display:flex;align-items:center;gap:7px;font-size:11px;font-weight:700;border-bottom:1px solid var(--border)}
.g-empty{padding:56px 24px;text-align:center;display:flex;flex-direction:column;align-items:center;gap:10px}
.g-ins-row{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
.g-ins{background:#fff;border:1px solid var(--border);border-radius:10px;padding:12px 15px;display:flex;align-items:center;gap:12px}
.g-ins-ic{width:36px;height:36px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0}
.g-charts{display:grid;grid-template-columns:1fr 1fr 210px;gap:12px}
.g-chrt{background:#fff;border:1px solid var(--border);border-radius:12px;overflow:hidden}
.g-chrt-h{padding:10px 14px;border-bottom:1px solid var(--border);background:#fafbfc;font-size:12px;font-weight:600}
.g-chrt-b{padding:12px 14px}
.g-sync{display:inline-flex;align-items:center;gap:5px;background:#d1fae5;color:#065f46;border:1px solid #6ee7b7;padding:3px 9px;border-radius:8px;font-size:10px;font-weight:600;margin-left:8px;vertical-align:middle}
.g-sdot{width:6px;height:6px;border-radius:50%;background:#059669;display:inline-block;animation:gp 2s infinite}
@keyframes gp{0%,100%{opacity:1}50%{opacity:.3}}
.g-alert{display:inline-flex;background:#fff5f5;color:#dc2626;border:1px solid #fca5a5;padding:1px 6px;border-radius:10px;font-size:9px;font-weight:700;margin-left:4px;vertical-align:middle}
.g-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:500;display:flex;align-items:flex-start;justify-content:center;padding:20px;overflow-y:auto}
.g-mbox{background:#fff;border-radius:16px;width:100%;max-width:640px;margin:auto;box-shadow:0 24px 64px rgba(0,0,0,.28);max-height:93vh;overflow-y:auto}
.g-mhdr{background:var(--grad);padding:14px 18px;border-radius:16px 16px 0 0;display:flex;justify-content:space-between;align-items:flex-start;position:sticky;top:0;z-index:10}
.g-mtitle{color:#fff;font-size:14px;font-weight:700}
.g-msub{color:rgba(255,255,255,.75);font-size:11px;margin-top:2px}
.g-mcls{width:28px;height:28px;border-radius:50%;border:none;background:rgba(255,255,255,.2);color:#fff;font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.g-mbody{padding:16px;display:flex;flex-direction:column;gap:12px}
.g-fsec{border-radius:10px;padding:12px}
.g-fs-info{background:#f0fdf4;border:1px solid #6ee7b7}
.g-fs-line{background:#eff6ff;border:1px solid #bfdbfe}
.g-fs-assoc{background:#f0fdf4;border:1px solid #86efac}
.g-fs-qty{background:#eff6ff;border:1px solid #93c5fd}
.g-fs-calc{background:#f8f9fa;border:1px solid var(--border)}
.g-flbl{font-size:11px;font-weight:700;margin-bottom:9px}
.g-mg2{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.g-mg3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px}
.g-mg4{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:10px}
.g-ff label{display:block;font-size:10px;font-weight:700;color:var(--muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:.4px}
.g-ff input,.g-ff select{width:100%;padding:8px 10px;border-radius:7px;border:1.5px solid var(--border);font-family:inherit;font-size:12px;background:#fff;color:var(--text);outline:none;transition:border-color .18s;box-sizing:border-box}
.g-ff select{cursor:pointer}
.g-ff input:focus,.g-ff select:focus{border-color:var(--golf);box-shadow:0 0 0 3px rgba(5,150,105,.08)}
.g-ff input.ro{background:#f0fdf4;border-color:#6ee7b7;color:#059669;font-weight:600}
.g-lbtns{display:flex;gap:8px}
.g-lbtn{flex:1;padding:10px;border-radius:8px;border:2px solid var(--border);background:#fff;font-family:inherit;font-size:12px;font-weight:700;cursor:pointer;transition:all .18s;display:flex;align-items:center;justify-content:center;gap:7px;color:var(--muted)}
.g-lbtn.g1.on{border-color:#1565c0;background:#eff6ff;color:#1565c0}
.g-lbtn.g2.on{border-color:#d97706;background:#fffbeb;color:#92400e}
.g-sbtns{display:flex;gap:6px}
.g-sbtn{flex:1;padding:8px;border-radius:7px;border:2px solid var(--border);background:#fff;color:var(--muted);font-family:inherit;font-size:11px;cursor:pointer;transition:all .18s}
.g-sbtn.on{border-color:var(--golf);background:#d1fae5;color:#065f46;font-weight:700}
.g-calc-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}
.g-calc-cell{background:#fff;border-radius:8px;padding:9px;border:1px solid var(--border);text-align:center}
.g-calc-lbl{font-size:9px;color:var(--muted);text-transform:uppercase;letter-spacing:.3px;margin-bottom:3px}
.g-calc-val{font-size:14px;font-weight:700;font-family:'DM Mono',monospace}
.g-mfoot{padding:11px 16px;border-top:1px solid var(--border);display:flex;gap:8px;background:#fafbfc;border-radius:0 0 16px 16px;position:sticky;bottom:0}
.g-btn-cancel{flex:1;padding:9px;border-radius:8px;border:1.5px solid var(--border);background:#fff;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer}
.g-btn-save{flex:2;padding:9px;border-radius:8px;border:none;background:var(--grad);color:#fff;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer}
.g-toast{position:fixed;bottom:22px;right:22px;padding:11px 18px;border-radius:9px;color:#fff;font-size:13px;font-weight:500;z-index:999;box-shadow:0 4px 18px rgba(0,0,0,.28);animation:gslide .22s ease}
@keyframes gslide{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
.g-toast.ok{background:#059669}.g-toast.warn{background:#d97706}.g-toast.err{background:#dc2626}
@media(max-width:700px){.g-kgrid{grid-template-columns:repeat(2,1fr)}.g-charts{grid-template-columns:1fr}.g-ins-row{grid-template-columns:1fr}}
`;

const CTORS = ['PRABHAS','DBS','KRISHNA','SHIVA','SWF','VENKATESWARA','SHUKOOR ISM','SAMUDRA'];
const emptyForm = () => ({
  golf_section:'GOLF 1', shift:'DAY',
  male_contractor:'PRABHAS', female_contractor:'DBS',
  male_associates:'', female_associates:'',
  total_lots:'', total_hon_count:'',
  total_hon_quantity:'', total_hl_quantity:'', grading_hl_quantity:'',
  working_hours:'', working_minutes:'0', remarks:'',
});

// ── Component ──────────────────────────────────────────────────────────────────
export default function GolfDashboard() {
  const { user }  = usePPCAuth();
  const navigate  = useNavigate();
  const isAdmin   = user?.role === 'admin';

  const [activeTab,     setActiveTab]     = useState('golf');
  const [golfPeriod,    setGolfPeriod]    = useState('daily');
  const [rptPeriod,     setRptPeriod]     = useState('daily');
  const [selectedDate,  setSelectedDate]  = useState(TODAY);
  const [shiftFilter,   setShiftFilter]   = useState('ALL');
  const [entries,       setEntries]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [apiOk,         setApiOk]         = useState(true);
  const [showModal,     setShowModal]     = useState(false);
  const [editId,        setEditId]        = useState(null);
  const [form,          setForm]          = useState(emptyForm());
  const [formErrors,    setFormErrors]    = useState({});

  // ── Lot-wise items ──────────────────────────────────────────────────────
  const emptyLot   = () => ({ id: Date.now() + Math.random(), lotNo: '', honCount: '', honQty: '', hlQty: '', gradingHlQty: '' });
  const [lotItems, setLotItems] = useState([emptyLot()]);
  const addLotItem    = () => setLotItems(p => [...p, emptyLot()]);
  const removeLotItem = (id) => { if (lotItems.length > 1) setLotItems(p => p.filter(l => l.id !== id)); };
  const updLotItem    = (id, key, val) => setLotItems(p => p.map(l => l.id === id ? { ...l, [key]: val } : l));
  const lotTotHonC = lotItems.reduce((s, l) => s + (parseInt(l.honCount)        || 0), 0);
  const lotTotHon  = lotItems.reduce((s, l) => s + (parseFloat(l.honQty)       || 0), 0);
  const lotTotHL   = lotItems.reduce((s, l) => s + (parseFloat(l.hlQty)        || 0), 0);
  const lotTotGHL  = lotItems.reduce((s, l) => s + (parseFloat(l.gradingHlQty) || 0), 0);
  const [saving,        setSaving]        = useState(false);
  const [showDel,       setShowDel]       = useState(false);
  const [delTarget,     setDelTarget]     = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [toast,         setToast]         = useState({ msg:'', type:'ok', show:false });
  const toastTimer  = useRef(null);
  const rTrendRef   = useRef(null); const rTrendCh  = useRef(null);
  const rBarRef     = useRef(null); const rBarCh    = useRef(null);
  const rGaugeRef   = useRef(null); const rGaugeCh  = useRef(null);

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type, show: true });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(s => ({ ...s, show: false })), 3000);
  };

  const setF = (k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
    setFormErrors(prev => ({ ...prev, [k]: '' }));
  };

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    const { from, to } = getPeriodRange(selectedDate, 'monthly');
    try {
      const res = await axios.get(
        `${PPC_API_URL}/golf.php?action=entries&date=${from}&to=${to}`, auth()
      );
      if (res.data?.success) { setEntries(res.data.entries || []); setApiOk(true); }
    } catch (_) { setApiOk(false); }
    finally { setLoading(false); }
  }, [selectedDate]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // ── Filter helpers ─────────────────────────────────────────────────────────
  const getFiltered = (per) => {
    const { from, to } = getPeriodRange(selectedDate, per);
    return entries.filter(r => {
      const d = r.entry_date || selectedDate;
      if (d < from || d > to) return false;
      if (shiftFilter !== 'ALL' && r.shift !== shiftFilter) return false;
      return true;
    });
  };

  // ── Charts (report tab) ────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'report') return;
    const rows = getFiltered(rptPeriod);
    const g1 = rows.filter(r => r.golf_section === 'GOLF 1');
    const g2 = rows.filter(r => r.golf_section === 'GOLF 2');
    const k1 = calcKPI(g1), k2 = calcKPI(g2);
    const k  = calcKPI(rows);
    const yC = k.yld >= 85 ? '#059669' : k.yld >= 70 ? '#d97706' : '#dc2626';

    const dates = [...new Set(rows.map(r => r.entry_date || selectedDate))].sort();
    const honD  = dates.map(d => rows.filter(r => (r.entry_date||selectedDate) === d).reduce((s,r) => s + parseFloat(r.total_hon_quantity||0), 0));
    const hlD   = dates.map(d => rows.filter(r => (r.entry_date||selectedDate) === d).reduce((s,r) => s + parseFloat(r.total_hl_quantity||0), 0));

    if (!window.Chart) return;
    if (rTrendCh.current) rTrendCh.current.destroy();
    if (rTrendRef.current) rTrendCh.current = new window.Chart(rTrendRef.current, {
      type: 'line',
      data: { labels: dates.map(fmtD), datasets: [
        { label:'H.ON Qty', data:honD, borderColor:'#1565c0', backgroundColor:'rgba(21,101,192,.08)', tension:.4, fill:true, pointRadius:3, pointBackgroundColor:'#1565c0', borderWidth:2 },
        { label:'HL Qty',   data:hlD,  borderColor:'#059669', backgroundColor:'rgba(5,150,105,.07)',  tension:.4, fill:true, pointRadius:3, pointBackgroundColor:'#059669', borderDash:[5,3], borderWidth:2 },
      ]},
      options: { responsive:true, maintainAspectRatio:false,
        plugins:{ legend:{ display:true, position:'top', labels:{ font:{ size:10 }, boxWidth:10, padding:10 } } },
        scales:{ x:{ grid:{ display:false }, ticks:{ font:{ size:9 } } },
                 y:{ grid:{ color:'rgba(0,0,0,.04)' }, ticks:{ font:{ size:9 }, callback:v=>v>=1000?(v/1000).toFixed(1)+'k':v } } } },
    });

    if (rBarRef.current) rBarRef.current.parentElement.style.height = '160px';
    if (rBarCh.current)  rBarCh.current.destroy();
    if (rBarRef.current)  rBarCh.current = new window.Chart(rBarRef.current, {
      type: 'bar',
      data: { labels:['GOLF 1','GOLF 2'], datasets:[
        { label:'H.ON Qty', data:[k1.hon, k2.hon], backgroundColor:'rgba(21,101,192,.78)', borderRadius:4, borderSkipped:false },
        { label:'HL Qty',   data:[k1.hl,  k2.hl],  backgroundColor:'rgba(5,150,105,.75)',  borderRadius:4, borderSkipped:false },
      ]},
      options: { responsive:true, maintainAspectRatio:false, indexAxis:'y',
        plugins:{ legend:{ display:true, position:'top', labels:{ font:{ size:10 }, boxWidth:10, padding:10 } } },
        scales:{ x:{ grid:{ color:'rgba(0,0,0,.04)' }, ticks:{ font:{ size:9 }, callback:v=>v>=1000?(v/1000)+'k':v } },
                 y:{ grid:{ display:false }, ticks:{ font:{ size:11, weight:'600' } } } } },
    });

    if (rGaugeCh.current) rGaugeCh.current.destroy();
    if (rGaugeRef.current) rGaugeCh.current = new window.Chart(rGaugeRef.current, {
      type: 'doughnut',
      data: { datasets:[{ data:[k.yld, Math.max(0,100-k.yld)], backgroundColor:[yC,'#f0f2f5'], borderWidth:0 }] },
      options: { responsive:true, maintainAspectRatio:false, circumference:270, rotation:225, cutout:'72%',
        plugins:{ legend:{ display:false }, tooltip:{ enabled:false } } },
    });
    const lbl = document.getElementById('g-gauge-lbl');
    if (lbl) { lbl.textContent = k.yld.toFixed(1) + '%'; lbl.style.color = yC; }
  }, [activeTab, rptPeriod, entries, selectedDate]); // eslint-disable-line

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const openAdd = (line = 'GOLF 1') => {
    setEditId(null);
    setForm({ ...emptyForm(), golf_section: line });
    setFormErrors({});
    setLotItems([emptyLot()]);
    setShowModal(true);
  };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      golf_section:        row.golf_section       || 'GOLF 1',
      shift:               row.shift              || 'DAY',
      male_contractor:     row.male_contractor    || 'PRABHAS',
      female_contractor:   row.female_contractor  || 'DBS',
      male_associates:     row.male_associates    ?? '',
      female_associates:   row.female_associates  ?? '',
      total_lots:          row.total_lots         ?? '',
      total_hon_count:     row.total_hon_count    ?? '',
      total_hon_quantity:  row.total_hon_quantity ?? '',
      total_hl_quantity:   row.total_hl_quantity  ?? '',
      grading_hl_quantity: row.grading_hl_quantity ?? '',
      working_hours:       row.working_hours      ?? '',
      working_minutes:     row.working_minutes    ?? '0',
      remarks:             row.remarks            || '',
    });
    setFormErrors({});
    const preLots = (row.lot_items && row.lot_items.length > 0)
      ? row.lot_items.map(l => ({ id: Date.now() + Math.random(), lotNo: l.lotNo || l.lot_no || '', honCount: String(l.honCount ?? l.hon_count ?? ''), honQty: String(l.honQty ?? l.total_hon_quantity ?? ''), hlQty: String(l.hlQty ?? l.total_hl_quantity ?? ''), gradingHlQty: String(l.gradingHlQty ?? l.grading_hl_quantity ?? '') }))
      : [{ id: Date.now(), lotNo: '', honCount: String(row.total_hon_count ?? ''), honQty: String(row.total_hon_quantity ?? ''), hlQty: String(row.total_hl_quantity ?? ''), gradingHlQty: String(row.grading_hl_quantity ?? '') }];
    setLotItems(preLots);
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditId(null); };

  // Auto-calc
  const fMale   = parseInt(form.male_associates)   || 0;
  const fFemale = parseInt(form.female_associates) || 0;
  const fHON    = parseFloat(form.total_hon_quantity) || 0;
  const fHL     = parseFloat(form.total_hl_quantity)  || 0;
  const fMins   = parseFloat(form.working_minutes) || 0;
  const fHrs    = (parseFloat(form.working_hours) || 0) + fMins / 60;
  const fTotal  = fMale + fFemale;
  const fYld    = fHON > 0 ? (fHL / fHON * 100) : 0;
  const fProd   = fHrs > 0 ? fHL / fHrs : 0;

  const validate = () => {
    const err = {};
    if (!form.golf_section) err.golf_section = 'Select a line';
    if (!form.shift)        err.shift = 'Shift is required';
    if (lotItems.some(l => !l.lotNo.trim())) err.lot_items = 'All Lot Numbers are required';
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload = {
      entry_date:          selectedDate,
      golf_section:        form.golf_section,
      shift:               form.shift,
      male_contractor:     form.male_contractor    || 'PRABHAS',
      female_contractor:   form.female_contractor  || 'DBS',
      male_associates:     parseInt(form.male_associates)    || 0,
      female_associates:   parseInt(form.female_associates)  || 0,
      total_associates:    fTotal,
      lot_items:           lotItems.map(l => ({ lot_no: l.lotNo.trim(), hon_count: parseInt(l.honCount)||0, hon_qty: parseFloat(l.honQty)||0, hl_qty: parseFloat(l.hlQty)||0, grading_hl_qty: parseFloat(l.gradingHlQty)||0 })),
      total_lots:          lotItems.length,
      total_hon_count:     lotTotHonC,
      total_hon_quantity:  lotTotHon,
      total_hl_quantity:   lotTotHL,
      grading_hl_quantity: lotTotGHL,
      yield_percent:       fYld,
      working_hours:       (parseFloat(form.working_hours) || 0) + (parseFloat(form.working_minutes) || 0) / 60,
      working_minutes:     parseInt(form.working_minutes) || 0,
      productivity:        fProd,
      remarks:             form.remarks || '',
    };
    if (apiOk) {
      try {
        let res;
        if (editId) {
          res = await axios.put(`${PPC_API_URL}/golf.php?action=edit-entry`, { entry_id: editId, ...payload }, auth());
        } else {
          res = await axios.post(`${PPC_API_URL}/golf.php?action=entries`, payload, auth());
        }
        if (res.data?.success) {
          showToast(editId ? 'Entry updated!' : 'Entry saved!');

          // FIX: Optimistic update — add/replace in entries state immediately,
          // so the table reflects data before the async re-fetch completes.
          if (editId) {
            setEntries(prev => prev.map(r => r.id === editId ? { ...r, ...payload, id: editId } : r));
          } else {
            const newId = res.data.id || (Date.now() + Math.random());
            setEntries(prev => [
              ...prev.filter(r => !(r.entry_date === payload.entry_date &&
                r.golf_section === payload.golf_section &&
                r.shift === payload.shift)),
              { ...payload, id: newId }
            ]);
          }

          closeModal(); fetchEntries(); setSaving(false); return;
        }
        showToast(res.data?.error || 'Save failed', 'err');
      } catch (err) { showToast(err.response?.data?.error || 'Failed to save', 'err'); }
      finally { setSaving(false); }
      return;
    }
    // Offline fallback
    if (editId) {
      setEntries(prev => prev.map(r => r.id === editId ? { ...r, ...payload } : r));
      showToast('Entry updated (offline)');
    } else {
      const newId = Math.max(0, ...entries.map(r => r.id || 0)) + 1;
      setEntries(prev => [...prev, { id: newId, ...payload }]);
      showToast('Entry saved (offline)');
    }
    closeModal(); setSaving(false);
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const openDel = (row) => { setDelTarget(row); setShowDel(true); };
  const closeDel = () => { setShowDel(false); setDelTarget(null); };
  const confirmDel = async () => {
    if (!delTarget) return;
    setDeleting(true);
    if (apiOk) {
      try {
        const res = await axios.delete(
          `${PPC_API_URL}/golf.php?action=reset&date=${delTarget.entry_date||selectedDate}&id=${delTarget.id}`, auth()
        );
        if (res.data?.success) {
          showToast('Entry deleted', 'warn'); closeDel(); fetchEntries(); setDeleting(false); return;
        }
      } catch (_) {}
      finally { setDeleting(false); }
    }
    setEntries(prev => prev.filter(r => r.id !== delTarget.id));
    showToast('Entry deleted (offline)', 'warn'); closeDel(); setDeleting(false);
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExport = () => {
    const per  = activeTab === 'report' ? rptPeriod : golfPeriod;
    const rows = getFiltered(per);
    if (!rows.length) { showToast('No data to export', 'warn'); return; }
    const headers = ['Section','Shift','Date','Male Contractor','Male Count','Female Contractor','Female Count',
      'Total Assoc','Total Lots','H.ON Count','H.ON Qty (KG)','HL Qty (KG)','Grading HL (KG)','Yield %','Machine Hrs'];
    const data = rows.map(r => {
      const hon = parseFloat(r.total_hon_quantity||0), hl = parseFloat(r.total_hl_quantity||0);
      const yld = hon > 0 ? (hl/hon*100) : 0;
      const ma  = parseInt(r.male_associates||0), fa = parseInt(r.female_associates||0);
      return [r.golf_section,r.shift,r.entry_date||selectedDate,
        r.male_contractor||'',ma,r.female_contractor||'',fa,ma+fa,
        r.total_lots||0,r.total_hon_count||0,hon.toFixed(2),hl.toFixed(2),
        parseFloat(r.grading_hl_quantity||0).toFixed(2),yld.toFixed(2),r.working_hours||0];
    });
    const { from, to } = getPeriodRange(selectedDate, per);
    const fname = per === 'daily' ? selectedDate : `${from}_to_${to}`;
    try {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([['Golf Dashboard — '+fname],[],headers,...data]);
      ws['!cols'] = headers.map(() => ({ wch: 14 }));
      XLSX.utils.book_append_sheet(wb, ws, 'Golf Entries');
      XLSX.writeFile(wb, `Golf_${fname}.xlsx`);
      showToast('Exported!');
    } catch (_) { showToast('Export failed','err'); }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const golfRows = getFiltered(golfPeriod);
  const g1       = golfRows.filter(r => r.golf_section === 'GOLF 1');
  const g2       = golfRows.filter(r => r.golf_section === 'GOLF 2');
  const rptRows  = getFiltered(rptPeriod);
  const rg1      = rptRows.filter(r => r.golf_section === 'GOLF 1');
  const rg2      = rptRows.filter(r => r.golf_section === 'GOLF 2');
  const kpi1     = calcKPI(g1),  kpi2  = calcKPI(g2);
  const rkpi1    = calcKPI(rg1), rkpi2 = calcKPI(rg2), rkpiAll = calcKPI(rptRows);
  const { from: gpFrom, to: gpTo } = getPeriodRange(selectedDate, golfPeriod);
  const { from: rpFrom, to: rpTo } = getPeriodRange(selectedDate, rptPeriod);
  const gRange = golfPeriod !== 'daily' ? `${fmtD(gpFrom)} — ${fmtD(gpTo)}` : '';
  const rRange = rptPeriod  !== 'daily' ? `${fmtD(rpFrom)} — ${fmtD(rpTo)}` : '';

  // ── KPI card helper ────────────────────────────────────────────────────────
  const KPICards = ({ kpi, accent }) => {
    const yC = kpi.yld >= 85 ? '#059669' : kpi.yld >= 70 ? '#d97706' : '#dc2626';
    const yCls = kpi.yld >= 85 ? 'g-kc-g' : kpi.yld >= 70 ? 'g-kc-a' : 'g-kc-r';
    return (
      <div className="g-kgrid">
        {[
          { l:'Total Lots', v:kpi.lots,                c:accent,   cl:'g-kc-a' },
          { l:'H.ON QTY',   v:kpi.hon.toFixed(2)+' KG',c:'#059669',cl:'g-kc-b' },
          { l:'HL QTY',     v:kpi.hl.toFixed(2)+' KG', c:'#059669',cl:'g-kc-g' },
          { l:'Yield %',    v:kpi.yld.toFixed(2)+'%',  c:yC,       cl:yCls },
          { l:'Associates', v:kpi.assoc,                c:'#7c3aed',cl:'g-kc-p' },
        ].map((s,i) => (
          <div key={i} className={`g-kc ${s.cl}`}>
            <div className="g-kc-lbl" style={{ color:s.c }}>{s.l}</div>
            <div className="g-kc-val" style={{ color:s.c }}>{s.v}</div>
          </div>
        ))}
      </div>
    );
  };

  const PeriodTabs = ({ value, onChange, range }) => (
    <div className="g-ptabs">
      {['daily','weekly','monthly'].map(p => (
        <button key={p} className={`g-ptab${value===p?' on':''}`} onClick={() => onChange(p)}>
          📅 {p.charAt(0).toUpperCase()+p.slice(1)}
        </button>
      ))}
      {range && <span className="g-range">{range}</span>}
    </div>
  );

  const ShiftTag = ({ shift }) => {
    const cls = shift === 'DAY' ? 'g-shift-d' : shift === 'GENERAL' ? 'g-shift-g' : 'g-shift-n';
    return <span className={cls}>{shift}</span>;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>
      <div className="gw">

        {/* Header */}
        <header className="g-hdr">
          <div className="g-hdr-l">
            <button className="g-hbtn" onClick={() => navigate('/ppc')}>← PPC Home</button>
            <span className="g-htitle">GOLF DASHBOARD</span>
            <span className="g-hbadge">{isAdmin ? '👑 Admin' : '👤 '+(user?.username||'User')}</span>
          </div>
          <div className="g-hdr-r">
            <input type="date" className="g-hdate" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
            <button className="g-hexp" onClick={handleExport}>📥 Export</button>
          </div>
        </header>

        {/* Nav */}
        <nav className="g-nav">
          {[
            { key:'golf',   label:'⛳ Golf Dashboard', cls:'ag', dot:'#059669' },
            { key:'report', label:'📊 Reports',         cls:'ar', dot:'#1565c0' },
          ].map(t => (
            <button key={t.key} className={`g-ntab${activeTab===t.key?' '+t.cls:''}`}
              onClick={() => setActiveTab(t.key)}>
              <span style={{ width:8,height:8,borderRadius:'50%',background:t.dot,display:'inline-block' }} />
              {t.label}
            </button>
          ))}
        </nav>

        {/* ══ GOLF DASHBOARD TAB ══ */}
        {activeTab === 'golf' && (
          <div className="g-main">
            <div>
              <span className="g-chip">⛳ SECTION 1 — GOLF DASHBOARD</span>
              <div className="g-title-row" style={{ marginTop:6 }}>
                <div>
                  <div style={{ fontSize:19,fontWeight:700 }}>Golf Dashboard</div>
                  <div style={{ fontSize:12,color:'var(--muted)',marginTop:2 }}>
                    Daily contractor-wise production entries — {fmtD(selectedDate)}
                  </div>
                </div>
                <div className="g-rp-wrap">
                  <span className="g-rp-lbl">📅 From:</span>
                  <input type="date" className="g-rp-date" value={gpFrom}
                    onChange={e => { setSelectedDate(e.target.value); setGolfPeriod('daily'); }} />
                  <span className="g-rp-lbl">To:</span>
                  <input type="date" className="g-rp-date" value={gpTo}
                    onChange={e => setSelectedDate(e.target.value)} />
                </div>
              </div>
            </div>

            <PeriodTabs value={golfPeriod} onChange={p => setGolfPeriod(p)} range={gRange} />

            {/* GOLF 1 */}
            <div className="g-sdiv">
              <span style={{ width:10,height:10,borderRadius:'50%',background:'#1565c0',flexShrink:0,display:'inline-block' }} />
              <span style={{ fontSize:13,fontWeight:700,color:'#1565c0',letterSpacing:.3 }}>GOLF 1</span>
              <div className="g-sdiv-line" />
            </div>
            <KPICards kpi={kpi1} accent="#1565c0" />

            {/* GOLF 2 */}
            <div className="g-sdiv">
              <span style={{ width:10,height:10,borderRadius:'50%',background:'#d97706',flexShrink:0,display:'inline-block' }} />
              <span style={{ fontSize:13,fontWeight:700,color:'#d97706',letterSpacing:.3 }}>GOLF 2</span>
              <div className="g-sdiv-line" />
            </div>
            <KPICards kpi={kpi2} accent="#d97706" />

            {/* Entries */}
            <div className="g-card">
              <div className="g-chdr">
                <div style={{ display:'flex',alignItems:'center',gap:10,flexWrap:'wrap' }}>
                  <span className="g-ctitle">
                    📋 Entries — {golfPeriod==='daily'?fmtD(selectedDate):gRange}
                  </span>
                  <div className="g-sfbtns">
                    {['ALL','DAY','GENERAL','NIGHT'].map(s => (
                      <button key={s} className={`g-sfbtn${shiftFilter===s?' on':''}`}
                        onClick={() => setShiftFilter(s)}>{s}</button>
                    ))}
                  </div>
                </div>
                <div style={{ display:'flex',gap:8 }}>
                  <button className="g-btn-g1" onClick={() => openAdd('GOLF 1')}>● GOLF 1</button>
                  <button className="g-btn-g2" onClick={() => openAdd('GOLF 2')}>● GOLF 2</button>
                  <button className="g-btn-exp" onClick={handleExport}>📥 Export</button>
                </div>
              </div>
              <div className="g-tbl-wrap">
                <table className="g-tbl">
                  <thead>
                    <tr>
                      <th className="tl" rowSpan={2} style={{ minWidth:100 }}>Contractor</th>
                      <th rowSpan={2}>Shift</th>
                      <th colSpan={3} style={{ background:'rgba(22,163,74,.08)',color:'#14532d' }}>Associates</th>
                      <th rowSpan={2} style={{ background:'#fffbeb',color:'#92400e' }}>Total Lots</th>
                      <th rowSpan={2} style={{ background:'#fffbeb',color:'#92400e' }}>H.ON Count</th>
                      <th colSpan={3} style={{ background:'rgba(21,101,192,.08)',color:'#1565c0' }}>Quantity (KG)</th>
                      <th rowSpan={2} style={{ background:'#f0fdf4',color:'#14532d' }}>Yield %</th>
                      <th rowSpan={2}>Mach. Hrs</th>
                      <th rowSpan={2} style={{ background:'#f8f9fa' }}>Actions</th>
                    </tr>
                    <tr>
                      <th style={{ background:'rgba(22,163,74,.08)',color:'#14532d',fontSize:9 }}>Male</th>
                      <th style={{ background:'rgba(22,163,74,.08)',color:'#14532d',fontSize:9 }}>Female</th>
                      <th style={{ background:'rgba(22,163,74,.08)',color:'#14532d',fontSize:9 }}>Total</th>
                      <th style={{ background:'rgba(21,101,192,.08)',color:'#1565c0',fontSize:9 }}>H.ON Qty</th>
                      <th style={{ background:'rgba(21,101,192,.08)',color:'#1565c0',fontSize:9 }}>HL Qty</th>
                      <th style={{ background:'rgba(21,101,192,.08)',color:'#1565c0',fontSize:9 }}>Yield</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={13} style={{ padding:32,textAlign:'center',color:'#9ca3af' }}>Loading…</td></tr>
                    ) : golfRows.length === 0 ? (
                      <tr><td colSpan={13}>
                        <div className="g-empty">
                          <div style={{ fontSize:48,opacity:.4 }}>⛳</div>
                          <div style={{ fontSize:14,fontWeight:600,color:'var(--muted)' }}>No entries for {fmtD(selectedDate)}</div>
                          <div style={{ fontSize:12,color:'#9ca3af' }}>Click GOLF 1 or GOLF 2 above to add entries</div>
                        </div>
                      </td></tr>
                    ) : (
                      <>
                        {/* GOLF 1 rows */}
                        {g1.length > 0 && (
                          <>
                            <tr><td colSpan={13} style={{ padding:0 }}>
                              <div className="g-sec-banner" style={{ background:'#eff6ff',color:'#1565c0' }}>
                                <span style={{ width:8,height:8,borderRadius:'50%',background:'#1565c0',display:'inline-block' }} />
                                GOLF 1 — {g1.length} entr{g1.length===1?'y':'ies'}
                              </div>
                            </td></tr>
                            {g1.map(r => <GolfRow key={r.id} r={r} onEdit={openEdit} onDel={openDel} />)}
                            <SectionTotal rows={g1} label="GOLF 1 TOTAL" color="#1565c0" bg="#f0f6ff" />
                          </>
                        )}
                        {/* GOLF 2 rows */}
                        {g2.length > 0 && (
                          <>
                            <tr><td colSpan={13} style={{ padding:0 }}>
                              <div className="g-sec-banner" style={{ background:'#fffbeb',color:'#92400e',borderTop:'1px solid var(--border)' }}>
                                <span style={{ width:8,height:8,borderRadius:'50%',background:'#d97706',display:'inline-block' }} />
                                GOLF 2 — {g2.length} entr{g2.length===1?'y':'ies'}
                              </div>
                            </td></tr>
                            {g2.map(r => <GolfRow key={r.id} r={r} onEdit={openEdit} onDel={openDel} />)}
                            <SectionTotal rows={g2} label="GOLF 2 TOTAL" color="#d97706" bg="#fffcf0" />
                          </>
                        )}
                      </>
                    )}
                  </tbody>
                  {golfRows.length > 0 && (() => {
                    const k = calcKPI(golfRows);
                    return (
                      <tfoot>
                        <tr>
                          <td className="tl" colSpan={2}>GRAND TOTAL ({golfRows.length})</td>
                          <td className="g-mono">{golfRows.reduce((s,r)=>s+parseInt(r.male_associates||0),0)}</td>
                          <td className="g-mono">{golfRows.reduce((s,r)=>s+parseInt(r.female_associates||0),0)}</td>
                          <td><span className="g-val-b">{k.assoc}</span></td>
                          <td className="g-mono">{k.lots}</td>
                          <td className="g-mono">{golfRows.reduce((s,r)=>s+parseInt(r.total_hon_count||0),0)}</td>
                          <td className="g-mono">{k.hon.toFixed(2)}</td>
                          <td><span className="g-val-g">{k.hl.toFixed(2)}</span></td>
                          <td className="g-mono">{k.grad.toFixed(2)}</td>
                          <td><span className={k.yld>=85?'g-val-g':k.yld>=70?'g-val-a':'g-val-r'}>{k.yld.toFixed(2)}%</span></td>
                          <td /><td />
                        </tr>
                      </tfoot>
                    );
                  })()}
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ══ REPORTS TAB ══ */}
        {activeTab === 'report' && (() => {
          const yC   = rkpiAll.yld >= 85 ? '#059669' : rkpiAll.yld >= 70 ? '#d97706' : '#dc2626';
          const top  = rkpi1.hl >= rkpi2.hl ? 'GOLF 1' : 'GOLF 2';
          const topK = rkpi1.hl >= rkpi2.hl ? rkpi1 : rkpi2;
          const low  = rkpi1.yld <= rkpi2.yld && rg1.length > 0 ? 'GOLF 1' : 'GOLF 2';
          const lowV = Math.min(rkpi1.yld, rkpi2.yld);
          const prev = rkpiAll.hl * 0.93;
          const chg  = ((rkpiAll.hl - prev) / prev * 100).toFixed(1);
          return (
            <div className="g-main">
              <PeriodTabs value={rptPeriod} onChange={p => setRptPeriod(p)} range={rRange} />
              <div>
                <span className="g-chip-r">📊 SECTION 2 — REPORTS</span>
                <div style={{ fontSize:19,fontWeight:700,marginTop:5 }}>
                  Golf Reports
                  <span className="g-sync"><span className="g-sdot" />Live sync</span>
                </div>
                <div style={{ fontSize:12,color:'var(--muted)',marginTop:2 }}>
                  Summary and analytics — {rptPeriod==='daily'?fmtD(selectedDate):rRange}
                </div>
              </div>

              {/* Combined KPI */}
              <div className="g-kgrid">
                {[
                  { l:'H.ON Qty',    v:rkpiAll.hon.toFixed(2)+' KG', c:'#1565c0', cl:'g-kc-b', s:'All sections' },
                  { l:'HL Qty',      v:rkpiAll.hl.toFixed(2)+' KG',  c:'#059669', cl:'g-kc-g', s:'All sections' },
                  { l:'Yield %',     v:rkpiAll.yld.toFixed(2)+'%',   c:yC,        cl:rkpiAll.yld>=85?'g-kc-g':rkpiAll.yld>=70?'g-kc-a':'g-kc-r', s:rkpiAll.yld>=85?'Above target':'Below 85%' },
                  { l:'Total Lots',  v:rkpiAll.lots,                 c:'#d97706', cl:'g-kc-a', s:'GOLF 1 + GOLF 2' },
                  { l:'Associates',  v:rkpiAll.assoc,                c:'#7c3aed', cl:'g-kc-p', s:'Male + female' },
                ].map((s,i) => (
                  <div key={i} className={`g-kc ${s.cl}`}>
                    <div className="g-kc-lbl" style={{ color:s.c }}>{s.l}</div>
                    <div className="g-kc-val" style={{ color:s.c }}>{s.v}</div>
                    {s.s && <div className="g-kc-sub">{s.s}</div>}
                  </div>
                ))}
              </div>

              {/* Insights */}
              <div className="g-ins-row">
                <div className="g-ins">
                  <div className="g-ins-ic" style={{ background:'#fff7ed' }}>🏆</div>
                  <div>
                    <div style={{ fontSize:9,color:'var(--muted)',textTransform:'uppercase',letterSpacing:.4,marginBottom:2,fontWeight:600 }}>Top Productivity</div>
                    <div style={{ fontSize:13,fontWeight:700 }}>{top}</div>
                    <div style={{ fontSize:10,color:'var(--muted)',marginTop:1 }}>{topK.hl.toFixed(1)} HL KG · {topK.prod.toFixed(2)} KG/hr</div>
                  </div>
                </div>
                <div className="g-ins">
                  <div className="g-ins-ic" style={{ background:'#fff5f5' }}>⚠️</div>
                  <div>
                    <div style={{ fontSize:9,color:'var(--muted)',textTransform:'uppercase',letterSpacing:.4,marginBottom:2,fontWeight:600 }}>Lowest Yield</div>
                    <div style={{ fontSize:13,fontWeight:700 }}>{low}</div>
                    <div style={{ fontSize:10,color:'var(--muted)',marginTop:1 }}>{lowV.toFixed(2)}% yield</div>
                  </div>
                </div>
                <div className="g-ins">
                  <div className="g-ins-ic" style={{ background:'#eff6ff' }}>📈</div>
                  <div>
                    <div style={{ fontSize:9,color:'var(--muted)',textTransform:'uppercase',letterSpacing:.4,marginBottom:2,fontWeight:600 }}>vs Previous Period</div>
                    <div style={{ fontSize:13,fontWeight:700,color:parseFloat(chg)>0?'#059669':'#dc2626' }}>{parseFloat(chg)>0?'+':''}{chg}%</div>
                    <div style={{ fontSize:10,color:'var(--muted)',marginTop:1 }}>{rkpiAll.hl.toFixed(0)} vs {prev.toFixed(0)} KG HL</div>
                  </div>
                </div>
              </div>

              {/* GOLF 1 KPIs */}
              <div className="g-sdiv">
                <span style={{ width:10,height:10,borderRadius:'50%',background:'#1565c0',flexShrink:0,display:'inline-block' }} />
                <span style={{ fontSize:13,fontWeight:700,color:'#1565c0' }}>GOLF 1 — Period Summary</span>
                <div className="g-sdiv-line" />
              </div>
              <KPICards kpi={rkpi1} accent="#1565c0" />

              {/* GOLF 2 KPIs */}
              <div className="g-sdiv">
                <span style={{ width:10,height:10,borderRadius:'50%',background:'#d97706',flexShrink:0,display:'inline-block' }} />
                <span style={{ fontSize:13,fontWeight:700,color:'#d97706' }}>GOLF 2 — Period Summary</span>
                <div className="g-sdiv-line" />
              </div>
              <KPICards kpi={rkpi2} accent="#d97706" />

              {/* Charts */}
              <div className="g-charts">
                <div className="g-chrt">
                  <div className="g-chrt-h">Production trend (H.ON vs HL)</div>
                  <div className="g-chrt-b">
                    <div style={{ position:'relative',height:180 }}><canvas ref={rTrendRef} role="img" aria-label="H.ON vs HL trend">trend</canvas></div>
                  </div>
                </div>
                <div className="g-chrt">
                  <div className="g-chrt-h">Section-wise quantity (KG)</div>
                  <div className="g-chrt-b">
                    <div style={{ position:'relative',height:160 }}><canvas ref={rBarRef} role="img" aria-label="Section bar">bar</canvas></div>
                  </div>
                </div>
                <div className="g-chrt">
                  <div className="g-chrt-h">Avg yield %</div>
                  <div className="g-chrt-b" style={{ display:'flex',flexDirection:'column',alignItems:'center',paddingTop:6 }}>
                    <div style={{ position:'relative',height:150,width:150 }}><canvas ref={rGaugeRef} role="img" aria-label="Yield gauge">gauge</canvas></div>
                    <div id="g-gauge-lbl" style={{ fontSize:22,fontWeight:700,fontFamily:'DM Mono,monospace',marginTop:-48,textAlign:'center' }}></div>
                    <div style={{ fontSize:10,color:'var(--muted)',marginTop:44,textAlign:'center' }}>Threshold: 85%</div>
                  </div>
                </div>
              </div>

              {/* Summary table */}
              <div className="g-card">
                <div className="g-chdr">
                  <span className="g-ctitle">📋 Section-wise Summary Report</span>
                  <button className="g-btn-exp" onClick={handleExport}>📥 Export</button>
                </div>
                <div className="g-tbl-wrap">
                  <table className="g-tbl">
                    <thead><tr>
                      <th className="tl">Section</th>
                      <th>Shift</th>
                      <th style={{ background:'rgba(21,101,192,.08)',color:'#1565c0' }}>H.ON Qty (KG)</th>
                      <th style={{ background:'rgba(21,101,192,.08)',color:'#1565c0' }}>HL Qty (KG)</th>
                      <th style={{ background:'rgba(21,101,192,.08)',color:'#1565c0' }}>Grading HL (KG)</th>
                      <th style={{ background:'#f0fdf4',color:'#14532d' }}>Yield %</th>
                      <th>Total Lots</th>
                      <th>Associates</th>
                      <th style={{ background:'#eff6ff',color:'#1565c0' }}>Prod (HL/Hr)</th>
                    </tr></thead>
                    <tbody>
                      {rptRows.length === 0 ? (
                        <tr><td colSpan={9} style={{ padding:28,textAlign:'center',color:'#9ca3af' }}>No data for this period</td></tr>
                      ) : (['GOLF 1','GOLF 2']).map(sec => {
                        const sRows = rptRows.filter(r => r.golf_section === sec);
                        if (!sRows.length) return null;
                        const shifts = [...new Set(sRows.map(r => r.shift))];
                        return shifts.map((sh, si) => {
                          const shRows = sRows.filter(r => r.shift === sh);
                          const kk = calcKPI(shRows);
                          const yc = kk.yld>=85?'g-val-g':kk.yld>=70?'g-val-a':'g-val-r';
                          return (
                            <tr key={sec+sh}>
                              {si === 0 && (
                                <td className="tl" rowSpan={shifts.length}
                                  style={{ color:sec==='GOLF 1'?'#1565c0':'#d97706',fontWeight:700,borderRight:'1px solid var(--border)' }}>
                                  {sec}
                                </td>
                              )}
                              <td><ShiftTag shift={sh} /></td>
                              <td className="g-mono">{kk.hon.toFixed(2)}</td>
                              <td><span className="g-val-g">{kk.hl.toFixed(2)}</span></td>
                              <td className="g-mono">{kk.grad.toFixed(2)}</td>
                              <td><span className={yc}>{kk.yld.toFixed(2)}%</span></td>
                              <td className="g-mono">{kk.lots}</td>
                              <td><span className="g-val-b">{kk.assoc}</span></td>
                              <td><span className="g-val-b">{kk.prod.toFixed(2)}</span></td>
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                    {rptRows.length > 0 && (
                      <tfoot><tr>
                        <td className="tl">TOTAL</td><td />
                        <td className="g-mono">{rkpiAll.hon.toFixed(2)}</td>
                        <td><span className="g-val-g" style={{ fontWeight:700 }}>{rkpiAll.hl.toFixed(2)}</span></td>
                        <td className="g-mono">{rkpiAll.grad.toFixed(2)}</td>
                        <td><span className={rkpiAll.yld>=85?'g-val-g':rkpiAll.yld>=70?'g-val-a':'g-val-r'} style={{ fontWeight:700 }}>{rkpiAll.yld.toFixed(2)}%</span></td>
                        <td className="g-mono">{rkpiAll.lots}</td>
                        <td><span className="g-val-b" style={{ fontWeight:700 }}>{rkpiAll.assoc}</span></td>
                        <td />
                      </tr></tfoot>
                    )}
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ══ ADD / EDIT MODAL ══ */}
        {showModal && (
          <div className="g-overlay" onClick={e => e.target.classList.contains('g-overlay') && closeModal()}>
            <div className="g-mbox" onClick={e => e.stopPropagation()}>
              <div className="g-mhdr">
                <div>
                  <div className="g-mtitle">{editId ? '✏️ Edit Entry' : '➕ Add Entry'} — Golf Dashboard</div>
                  <div className="g-msub">{form.golf_section} · {fmtD(selectedDate)} · {form.shift} shift</div>
                </div>
                <button className="g-mcls" onClick={closeModal}>×</button>
              </div>
              <div className="g-mbody">
                {/* Date & Contractors */}
                <div className="g-fsec g-fs-info">
                  <div className="g-flbl" style={{ color:'#059669' }}>📅 Date &amp; Contractors</div>
                  <div className="g-mg3">
                    <div className="g-ff"><label>Entry Date</label>
                      <input type="date" value={selectedDate} readOnly className="ro" style={{ fontFamily:'inherit' }} />
                    </div>
                    <div className="g-ff"><label>Male Contractor</label>
                      <select value={form.male_contractor} onChange={e => setF('male_contractor',e.target.value)} style={{ fontFamily:'inherit' }}>
                        {CTORS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="g-ff"><label>Female Contractor</label>
                      <select value={form.female_contractor} onChange={e => setF('female_contractor',e.target.value)} style={{ fontFamily:'inherit' }}>
                        {CTORS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                {/* Select Line */}
                <div className="g-fsec g-fs-line">
                  <div className="g-flbl" style={{ color:'#1565c0' }}>🏭 Select Line *</div>
                  <div className="g-lbtns">
                    {['GOLF 1','GOLF 2'].map(l => (
                      <button key={l} type="button"
                        className={`g-lbtn ${l==='GOLF 1'?'g1':'g2'}${form.golf_section===l?' on':''}`}
                        onClick={() => setF('golf_section',l)}>
                        <span style={{ width:9,height:9,borderRadius:'50%',background:l==='GOLF 1'?'#1565c0':'#d97706',display:'inline-block' }} />
                        {l}
                      </button>
                    ))}
                  </div>
                  {formErrors.golf_section && <div style={{ fontSize:10,color:'#dc2626',marginTop:4 }}>{formErrors.golf_section}</div>}
                </div>
                {/* Shift */}
                <div className="g-fsec g-fs-info">
                  <div className="g-flbl" style={{ color:'#059669' }}>⏰ Shift *</div>
                  <div className="g-sbtns">
                    {['DAY','GENERAL','NIGHT'].map(s => (
                      <button key={s} type="button"
                        className={`g-sbtn${form.shift===s?' on':''}`}
                        onClick={() => setF('shift',s)}>
                        {s==='DAY'?'☀️':s==='GENERAL'?'🔄':'🌙'} {s}
                      </button>
                    ))}
                  </div>
                  {formErrors.shift && <div style={{ fontSize:10,color:'#dc2626',marginTop:4 }}>{formErrors.shift}</div>}
                </div>
                {/* Associates */}
                <div className="g-fsec g-fs-assoc">
                  <div className="g-flbl" style={{ color:'#059669' }}>👷 Associates</div>
                  <div className="g-mg3">
                    <div className="g-ff"><label>Supporting Male</label>
                      <input type="number" value={form.male_associates} onChange={e => setF('male_associates',e.target.value)} min={0} placeholder="No." onWheel={e=>e.target.blur()} />
                    </div>
                    <div className="g-ff"><label>Supporting Female</label>
                      <input type="number" value={form.female_associates} onChange={e => setF('female_associates',e.target.value)} min={0} placeholder="No." onWheel={e=>e.target.blur()} />
                    </div>
                    <div className="g-ff"><label>Total (Auto)</label>
                      <input type="number" value={fTotal||''} readOnly className="ro" />
                    </div>
                  </div>
                </div>
                {/* ✅ LOT & Quantity — lot-wise with Add More */}
                <div className="g-fsec g-fs-qty">
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                    <div className="g-flbl" style={{ color:'#1565c0', marginBottom:0, display:'flex', alignItems:'center', gap:7 }}>
                      📦 LOT &amp; Quantity
                      <span style={{ background:'#d97706', color:'#fff', fontSize:9, padding:'2px 9px', borderRadius:10, fontWeight:700 }}>
                        {lotItems.length} lot{lotItems.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <button type="button" onClick={addLotItem}
                      style={{ display:'flex', alignItems:'center', gap:5, padding:'5px 12px', border:'1.5px dashed #d97706', borderRadius:7, background:'#fffbeb', color:'#92400e', fontFamily:'inherit', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                      ＋ Add More
                    </button>
                  </div>

                  {formErrors.lot_items && (
                    <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:7, padding:'7px 11px', fontSize:11, color:'#dc2626', marginBottom:10 }}>
                      ⚠️ {formErrors.lot_items}
                    </div>
                  )}

                  {lotItems.map((lot, idx) => (
                    <div key={lot.id} style={{ background:'#fff', borderRadius:9, border:'1.5px solid #93c5fd', padding:'10px 12px', marginBottom:8, position:'relative', paddingTop: lotItems.length > 1 ? 22 : 10 }}>
                      {lotItems.length > 1 && (
                        <span style={{ position:'absolute', top:-9, left:12, background:'#1565c0', color:'#fff', fontSize:9, fontWeight:700, padding:'2px 10px', borderRadius:10 }}>
                          LOT {idx + 1}
                        </span>
                      )}
                      {lotItems.length > 1 && (
                        <button type="button" onClick={() => removeLotItem(lot.id)}
                          style={{ position:'absolute', top:7, right:8, width:22, height:22, borderRadius:'50%', border:'1px solid #fca5a5', background:'#fee2e2', color:'#dc2626', fontSize:14, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1, fontFamily:'inherit' }}>
                          ×
                        </button>
                      )}
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:9 }}>
                        <div className="g-ff">
                          <label>Lot Number <span style={{ color:'#dc2626' }}>*</span></label>
                          <input type="text" value={lot.lotNo} placeholder="e.g. LOT-001"
                            onChange={e => updLotItem(lot.id, 'lotNo', e.target.value)}
                            style={{ fontFamily:'DM Mono,monospace', fontSize:12, borderColor: (!lot.lotNo.trim() && formErrors.lot_items) ? '#fca5a5' : undefined }} />
                        </div>
                        <div className="g-ff">
                          <label>H.ON Count</label>
                          <input type="number" value={lot.honCount} min={0} placeholder=""
                            onChange={e => updLotItem(lot.id, 'honCount', e.target.value)} onWheel={e => e.target.blur()} />
                        </div>
                      </div>
                      <div className="g-mg3">
                        <div className="g-ff">
                          <label>H.ON Qty (KG)</label>
                          <input type="number" value={lot.honQty} min={0} step={0.01} placeholder=""
                            onChange={e => updLotItem(lot.id, 'honQty', e.target.value)} onWheel={e => e.target.blur()} />
                        </div>
                        <div className="g-ff">
                          <label>HL Qty (KG)</label>
                          <input type="number" value={lot.hlQty} min={0} step={0.01} placeholder=""
                            onChange={e => updLotItem(lot.id, 'hlQty', e.target.value)} onWheel={e => e.target.blur()} />
                        </div>
                        {/* ADDED: auto-calculated Yield % per lot — HL ÷ H.ON × 100 (matches modal aggregate fYld) */}
                        <div className="g-ff">
                          <label>Yield %</label>
                          {(() => {
                            const hon = parseFloat(lot.honQty) || 0;
                            const hl  = parseFloat(lot.hlQty)  || 0;
                            const y   = hon > 0 ? (hl / hon * 100) : null;
                            const col = y === null ? '#9ca3af' : y >= 85 ? '#059669' : y >= 70 ? '#d97706' : '#dc2626';
                            return (
                              <input type="text" readOnly tabIndex={-1}
                                value={y === null ? '' : y.toFixed(2) + '%'}
                                placeholder="—"
                                style={{ background:'#f9fafb', color:col, fontWeight:700, cursor:'not-allowed' }} />
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* ∑ Lot Totals strip */}
                  <div style={{ background:'#eff6ff', borderRadius:8, border:'1px solid #93c5fd', padding:'9px 11px', marginBottom:10 }}>
                    <div style={{ fontSize:9, fontWeight:700, color:'#1565c0', textTransform:'uppercase', letterSpacing:'.4px', marginBottom:7 }}>∑ Lot Totals</div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:7 }}>
                      {[
                        { l:'H.ON Count', v: lotTotHonC,            c:'#d97706' },
                        { l:'H.ON Qty',   v: lotTotHon.toFixed(2),  c:'#1565c0' },
                        { l:'HL Qty',     v: lotTotHL.toFixed(2),   c:'#059669' },
                        { l:'Grading HL', v: lotTotGHL.toFixed(2),  c:'#7c3aed' },
                      ].map((t, i) => (
                        <div key={i} style={{ background:'#fff', borderRadius:7, padding:'7px 8px', border:'1px solid #e2e6ea', textAlign:'center' }}>
                          <div style={{ fontSize:9, color:'#6b7280', textTransform:'uppercase', letterSpacing:'.3px', marginBottom:3 }}>{t.l}</div>
                          <div style={{ fontSize:13, fontWeight:700, fontFamily:'DM Mono,monospace', color:t.c }}>{t.v}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Machine Working Time — Hours + Minutes styled like image */}
                  <div style={{ background:'#f0fdf4', border:'1.5px solid #6ee7b7', borderRadius:10, padding:'11px 13px', marginTop:10 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                      <span style={{ fontSize:14 }}>⏱️</span>
                      <span style={{ fontSize:12, fontWeight:700, color:'#065f46' }}>Machine Working Time</span>
                      <span style={{ background:'#059669', color:'#fff', fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>Updated</span>
                    </div>
                    <div className="g-mg2" style={{ marginBottom:10 }}>
                      <div className="g-ff">
                        <label style={{ display:'flex', alignItems:'center', gap:6 }}>
                          Machine Working Hours
                          <span style={{ background:'#059669', color:'#fff', fontSize:8, fontWeight:700, padding:'1px 6px', borderRadius:10 }}>HRS</span>
                        </label>
                        <input type="number" value={form.working_hours} onChange={e => setF('working_hours', e.target.value)}
                          min={0} step={1} placeholder="e.g. 8" onWheel={e=>e.target.blur()}
                          style={{ border:'1.5px solid #6ee7b7', borderRadius:8, background:'#fff', fontSize:13, fontFamily:'DM Mono,monospace', fontWeight:500 }} />
                      </div>
                      <div className="g-ff">
                        <label style={{ display:'flex', alignItems:'center', gap:6 }}>
                          Machine Working Minutes
                          <span style={{ background:'#059669', color:'#fff', fontSize:8, fontWeight:700, padding:'1px 6px', borderRadius:10 }}>MIN</span>
                        </label>
                        <input type="number" value={form.working_minutes}
                          onChange={e => { const v = Math.min(59, Math.max(0, parseInt(e.target.value) || 0)); setF('working_minutes', String(v)); }}
                          min={0} max={59} step={1} placeholder="0" onWheel={e=>e.target.blur()}
                          style={{ border:'1.5px solid #6ee7b7', borderRadius:8, background:'#fff', fontSize:13, fontFamily:'DM Mono,monospace', fontWeight:500 }} />
                      </div>
                    </div>
                    {/* Total Machine Time preview row */}
                    <div style={{ background:'#fff', border:'1px solid #6ee7b7', borderRadius:8, padding:'8px 13px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <span style={{ fontSize:11, color:'#065f46', fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
                        <span style={{ fontSize:13 }}>⏱</span> Total Machine Time
                      </span>
                      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <span style={{ fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:700, color: fHrs > 0 ? '#059669' : '#9ca3af' }}>
                          {Math.floor(parseFloat(form.working_hours) || 0)}h {parseInt(form.working_minutes) || 0}m
                        </span>
                        <span style={{ fontFamily:'DM Mono,monospace', fontSize:11, color:'#9ca3af' }}>= {fHrs.toFixed(2)} hrs</span>
                      </div>
                    </div>
                  </div>
                  <div className="g-mg2" style={{ marginTop:10 }}>
                    <div className="g-ff"><label>Remarks</label>
                      <input type="text" value={form.remarks} onChange={e => setF('remarks',e.target.value)} placeholder="Optional" style={{ fontFamily:'inherit' }} />
                    </div>
                  </div>
                </div>
                {/* Auto Calc */}
                <div className="g-fsec g-fs-calc">
                  <div className="g-flbl" style={{ color:'var(--muted)' }}>🔢 Auto Calculations</div>
                  <div className="g-calc-grid">
                    <div className="g-calc-cell"><div className="g-calc-lbl">Total Assoc</div><div className="g-calc-val" style={{ color:'#059669' }}>{fTotal}</div></div>
                    <div className="g-calc-cell"><div className="g-calc-lbl">Yield %</div><div className="g-calc-val" style={{ color:fYld>=85?'#059669':fYld>=70?'#d97706':'#dc2626' }}>{fHON>0?fYld.toFixed(2)+'%':'—'}</div></div>
                    <div className="g-calc-cell"><div className="g-calc-lbl">Prod (HL/Hr)</div><div className="g-calc-val" style={{ color:'#1565c0' }}>{fHrs>0?fProd.toFixed(2):'—'}</div></div>
                    <div className="g-calc-cell"><div className="g-calc-lbl">Overall Yield</div><div className="g-calc-val" style={{ color:fYld>=85?'#059669':'#d97706' }}>{fHON>0?fYld.toFixed(2)+'%':'—'}</div></div>
                  </div>
                </div>
              </div>
              <div className="g-mfoot">
                <button className="g-btn-cancel" onClick={closeModal}>Cancel</button>
                <button className="g-btn-save" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : editId ? '💾 Update Entry' : '💾 Save Entry'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete modal */}
        {showDel && delTarget && (
          <div className="g-overlay" onClick={e => e.target.classList.contains('g-overlay') && closeDel()}>
            <div style={{ background:'#fff',borderRadius:16,padding:'30px 24px',width:'100%',maxWidth:380,margin:'auto',textAlign:'center' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ fontSize:44,marginBottom:14 }}>🗑️</div>
              <div style={{ fontSize:17,fontWeight:700,color:'#dc2626',marginBottom:6 }}>Delete Entry?</div>
              <div style={{ fontSize:14,fontWeight:700,marginBottom:14 }}>
                {delTarget.golf_section} — {delTarget.shift} — {fmtD(delTarget.entry_date||selectedDate)}
              </div>
              <div style={{ fontSize:11,color:'#dc2626',background:'#fff5f5',border:'1px solid #fecaca',borderRadius:7,padding:'9px 12px',marginBottom:18,textAlign:'left' }}>
                ⚠️ This action cannot be undone.
              </div>
              <div style={{ display:'flex',gap:10 }}>
                <button className="g-btn-cancel" onClick={closeDel}>Cancel</button>
                <button onClick={confirmDel} disabled={deleting}
                  style={{ flex:2,padding:11,borderRadius:8,border:'none',background:'linear-gradient(135deg,#dc2626,#ef4444)',color:'#fff',fontFamily:'inherit',fontSize:13,fontWeight:700,cursor:deleting?'not-allowed':'pointer',opacity:deleting?.6:1 }}>
                  {deleting ? 'Deleting…' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {toast.show && <div className={`g-toast ${toast.type}`}>{toast.msg}</div>}
      </div>
    </>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────
function GolfRow({ r, onEdit, onDel }) {
  const hon   = parseFloat(r.total_hon_quantity || 0);
  const hl    = parseFloat(r.total_hl_quantity  || 0);
  const yld   = hon > 0 ? (hl / hon * 100) : 0;
  const yc    = yld >= 85 ? 'g-val-g' : yld >= 70 ? 'g-val-a' : 'g-val-r';
  const sc    = r.shift === 'DAY' ? 'g-shift-d' : r.shift === 'GENERAL' ? 'g-shift-g' : 'g-shift-n';
  const assoc = parseInt(r.male_associates||0) + parseInt(r.female_associates||0);
  return (
    <tr>
      <td className="tl">
        {r.male_contractor||'—'}
        <br /><span style={{ fontSize:10,color:'var(--muted)',fontWeight:400 }}>{r.female_contractor||'—'}</span>
      </td>
      <td><span className={sc}>{r.shift}</span></td>
      <td className="g-mono">{r.male_associates||0}</td>
      <td className="g-mono">{r.female_associates||0}</td>
      <td><span className="g-val-b">{assoc}</span></td>
      <td>
        <div style={{ fontFamily:'DM Mono,monospace', fontSize:11 }}>{r.total_lots||0}</div>
        {r.lot_items && r.lot_items.length > 0 && (
          <div style={{ marginTop:3 }}>
            {r.lot_items.map((l, i) => (
              <span key={i} style={{ display:'inline-flex', background:'#fffbeb', border:'1px solid #fde68a', color:'#92400e', borderRadius:5, padding:'1px 5px', fontFamily:'DM Mono,monospace', fontSize:8, fontWeight:700, marginRight:2, marginBottom:2 }}>
                {l.lot_no || l.lotNo}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="g-mono">{r.total_hon_count||0}</td>
      <td className="g-mono">{hon.toFixed(2)}</td>
      <td><span className="g-val-g">{hl.toFixed(2)}</span></td>
      <td className="g-mono">{parseFloat(r.grading_hl_quantity||0).toFixed(2)}</td>
      <td><span className={yc}>{yld.toFixed(2)}%</span></td>
      <td className="g-mono">
        {(() => {
          const totalH = parseFloat(r.working_hours) || 0;
          const h = Math.floor(totalH);
          const m = r.working_minutes != null ? parseInt(r.working_minutes) : Math.round((totalH - h) * 60);
          return m > 0 ? `${h}h ${m}m` : `${h}h`;
        })()}
      </td>
      <td>
        <div style={{ display:'flex',gap:3,justifyContent:'center' }}>
          <button className="g-bsm" style={{ background:'#1565c0' }} onClick={() => onEdit(r)}>EDIT</button>
          <button className="g-bsm" style={{ background:'#dc2626' }} onClick={() => onDel(r)}>DEL</button>
        </div>
      </td>
    </tr>
  );
}

function SectionTotal({ rows, label, color, bg }) {
  const k = calcKPI(rows);
  return (
    <tr style={{ background:bg }}>
      <td className="tl" colSpan={2} style={{ fontSize:10,fontWeight:700,color }}>{label}</td>
      <td className="g-mono">{rows.reduce((s,r)=>s+parseInt(r.male_associates||0),0)}</td>
      <td className="g-mono">{rows.reduce((s,r)=>s+parseInt(r.female_associates||0),0)}</td>
      <td style={{ textAlign:'center',fontFamily:'DM Mono,monospace',fontWeight:600,color:'#1565c0' }}>{k.assoc}</td>
      <td className="g-mono">{k.lots}</td>
      <td className="g-mono">{rows.reduce((s,r)=>s+parseInt(r.total_hon_count||0),0)}</td>
      <td className="g-mono">{k.hon.toFixed(2)}</td>
      <td style={{ textAlign:'center',fontFamily:'DM Mono,monospace',fontWeight:600,color:'#059669' }}>{k.hl.toFixed(2)}</td>
      <td className="g-mono">{k.grad.toFixed(2)}</td>
      <td style={{ textAlign:'center',fontFamily:'DM Mono,monospace',fontWeight:600,color:k.yld>=85?'#059669':k.yld>=70?'#d97706':'#dc2626' }}>{k.yld.toFixed(2)}%</td>
      <td /><td />
    </tr>
  );
}
